import React from "react";
import {
  AlertTriangle, Bike, CalendarDays, Clock, Coins, Gauge, MapPin, Receipt, Route, Snail, Timer, Zap,
} from "lucide-react";

import { formatCurrency, formatInt, formatPct } from "@/features/relatorio-vendas/lib/formato";

import { Destaques, Kpi, Nota, Secao } from "../componentes/Base";
import Faixas from "../componentes/Faixas";
import GraficoMetrica from "../componentes/GraficoMetrica";
import LinhaDoTempo from "../componentes/LinhaDoTempo";
import Tabela from "../componentes/Tabela";
import {
  META_MINUTOS, ateMeta, comparador, formatarDecimal, formatarMinutos, gargalo, reaisEixo, variacao,
} from "../regras";

const minutosEixo = (v) => `${Math.round(v)} min`;

// Texto por canal: o fim da viagem é "entrega" no delivery e "o cliente buscar" na retirada
const TEXTOS = {
  delivery: { total: "Tempo até entregar", metade: "Metade chega em até", meta: META_MINUTOS.delivery },
  ifood: { total: "Tempo até entregar", metade: "Metade chega em até", meta: META_MINUTOS.ifood },
  retirada: { total: "Tempo até retirar", metade: "Metade é retirada em até", meta: META_MINUTOS.retirada },
};

const COLUNAS_PESSOA = (rotulo) => [
  { key: "nome", rotulo, render: (l) => <span className="font-semibold">{l.nome}</span> },
  { key: "vendas", rotulo: "Pedidos", formatar: formatInt, barra: true },
  { key: "tempo_medio", rotulo: "Tempo total", formatar: formatarMinutos, dica: "Do pedido até a entrega" },
  { key: "rua_media", rotulo: "Na rua", formatar: formatarMinutos, dica: "Da saída até a entrega" },
  { key: "faturamento", rotulo: "Faturou", formatar: formatCurrency },
  { key: "ticket_medio", rotulo: "Ticket", formatar: formatCurrency },
];

function destaques(d, canal) {
  const lento = gargalo(d.etapas);
  const relevantes = d.por_hora.filter((h) => h.pedidos >= Math.max(3, d.kpis.pedidos * 0.04) && h.tempo_medio != null);
  const horaLenta = [...relevantes].sort((a, b) => b.tempo_medio - a.tempo_medio)[0];
  const horaRapida = [...relevantes].sort((a, b) => a.tempo_medio - b.tempo_medio)[0];
  const motoboys = d.entregadores.filter((e) => e.vendas >= 5 && e.rua_media != null).sort((a, b) => a.rua_media - b.rua_media);
  const bairroLento = [...d.bairros].filter((b) => b.vendas >= 5 && b.tempo_medio != null).sort((a, b) => b.tempo_medio - a.tempo_medio)[0];
  return [
    lento && { icone: AlertTriangle, cor: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      texto: <>A etapa que mais demora é <b>{lento.rotulo.toLowerCase()}</b>: {formatarMinutos(lento.tempo_medio)} em média.</> },
    horaLenta && horaRapida && horaLenta.hora !== horaRapida.hora && { icone: Snail,
      texto: <>Às <b>{horaLenta.rotulo}</b> o pedido leva {formatarMinutos(horaLenta.tempo_medio)}; às <b>{horaRapida.rotulo}</b>, {formatarMinutos(horaRapida.tempo_medio)}.</> },
    canal !== "retirada" && motoboys.length > 1 && { icone: Zap, cor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      texto: <><b>{motoboys[0].nome}</b> é o entregador mais rápido na rua: {formatarMinutos(motoboys[0].rua_media)} por entrega.</> },
    canal !== "retirada" && bairroLento && { icone: MapPin,
      texto: <>O bairro mais demorado é <b>{bairroLento.nome}</b>: {formatarMinutos(bairroLento.tempo_medio)} do pedido à entrega.</> },
  ];
}

// Relatório de Tempo → Delivery / Retirada / iFood: quanto tempo cada etapa do pedido leva.
export default function EntregasAba({ dados, canal }) {
  const k = dados.kpis;
  const textos = TEXTOS[canal];
  if (!k.pedidos) return <Nota>Nenhum pedido de {dados.nome} nesse período.</Nota>;
  const pctMeta = ateMeta(dados.faixas, textos.meta);
  const ant = dados.anterior;
  const cmp = comparador(k, ant?.kpis);
  const pctMetaAntes = ant ? ateMeta(ant.faixas, textos.meta) : null;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-e2e={`kpis-${canal}`}>
        <Kpi destaque rotulo={textos.total} icone={Timer} valor={formatarMinutos(k.tempo_medio)}
          {...cmp("tempo_medio", formatarMinutos, { tipo: "min", melhor: "menos" })}
          detalhe={k.tempo_mediano != null ? `${textos.metade} ${formatarMinutos(k.tempo_mediano)}` : null} />
        <Kpi rotulo={`Até ${textos.meta} min`} icone={Gauge} valor={pctMeta == null ? "—" : formatPct(pctMeta, 0)}
          variacao={variacao(pctMeta, pctMetaAntes, { tipo: "pontos" })} antes={pctMetaAntes == null ? null : formatPct(pctMetaAntes, 0)}
          detalhe={`Dos pedidos com tempo, quantos ficaram em até ${textos.meta} min`} />
        <Kpi rotulo="Faturamento" icone={Coins} valor={formatCurrency(k.faturamento)} detalhe={`${formatInt(k.pedidos)} pedidos`}
          {...cmp("faturamento", formatCurrency)} />
        <Kpi rotulo="Ticket médio" icone={Receipt} valor={formatCurrency(k.ticket_medio)} {...cmp("ticket_medio", formatCurrency)}
          detalhe={k.itens_media != null ? `${formatarDecimal(k.itens_media)} itens por pedido` : null} />
      </div>

      <Secao titulo="A viagem do pedido" icone={Route} e2e="viagem-pedido"
        explicacao="Tempo médio de cada etapa, da chegada do pedido até o fim. O pedaço maior é onde o tempo vai embora.">
        <LinhaDoTempo etapas={dados.etapas} />
      </Secao>

      <Destaques itens={destaques(dados, canal)} />

      <GraficoMetrica titulo="Hora a hora" icone={Clock} e2e={`horas-${canal}`} dados={dados.por_hora}
        explicacao="Pela hora em que o pedido chegou."
        metricas={[
          { key: "tempo_medio", rotulo: "Tempo total", formatar: formatarMinutos, eixo: minutosEixo,
            explicacao: "Quanto o pedido leva, em média, conforme a hora em que chegou." },
          { key: "pedidos", rotulo: "Pedidos", formatar: formatInt },
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo },
          { key: "ticket_medio", rotulo: "Ticket", formatar: formatCurrency, eixo: reaisEixo },
        ]} />

      <Faixas faixas={dados.faixas} titulo="Quanto tempo o pedido leva"
        explicacao="Quantos pedidos caem em cada faixa de tempo total." />

      <GraficoMetrica titulo="Dia da semana" icone={CalendarDays} e2e={`dias-${canal}`} dados={dados.por_dia_semana}
        metricas={[
          { key: "tempo_medio", rotulo: "Tempo total", formatar: formatarMinutos, eixo: minutosEixo },
          { key: "vendas", rotulo: "Pedidos", formatar: formatInt },
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo },
        ]} />

      {canal !== "retirada" && dados.entregadores.length > 0 && (
        <Secao titulo="Entregadores" icone={Bike} e2e={`entregadores-${canal}`}
          explicacao="Os que mais entregaram no período. “Na rua” é da saída até a entrega — a parte que depende do entregador.">
          <Tabela linhas={dados.entregadores} colunas={COLUNAS_PESSOA("Entregador")} ordemInicial={{ key: "vendas", dir: "desc" }} />
        </Secao>
      )}

      {canal !== "retirada" && dados.bairros.length > 0 && (
        <Secao titulo="Bairros" icone={MapPin} e2e={`bairros-${canal}`} explicacao="Os bairros com mais pedidos e quanto tempo cada um leva.">
          <Tabela linhas={dados.bairros} colunas={COLUNAS_PESSOA("Bairro")} ordemInicial={{ key: "vendas", dir: "desc" }} />
        </Secao>
      )}

      {dados.mais_demorados.length > 0 && (
        <Secao titulo="Pedidos mais demorados" icone={Snail} e2e={`demorados-${canal}`}
          explicacao="Os 10 que mais levaram para fechar, e em que etapa ficaram parados.">
          <div className="divide-y">
            {dados.mais_demorados.map((p) => {
              const etapa = dados.etapas.find((e) => e.key === p.gargalo);
              const quando = p.criado_em ? new Date(p.criado_em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <div key={p.codigo || p.criado_em} className="flex items-center gap-3 py-2 text-sm">
                  <span className="font-mono text-xs font-semibold w-16 shrink-0">#{p.codigo}</span>
                  <span className="text-xs text-muted-foreground w-24 shrink-0 tabular-nums">{quando}</span>
                  <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate">
                    {etapa ? <>Parado em <b className="text-foreground">{etapa.rotulo.toLowerCase()}</b> ({formatarMinutos(p.gargalo_min)})</> : null}
                    {p.bairro ? ` · ${p.bairro}` : ""}
                  </span>
                  <span className="font-bold tabular-nums shrink-0">{formatarMinutos(p.total_min)}</span>
                </div>
              );
            })}
          </div>
        </Secao>
      )}

      <div className="space-y-1">
        {canal === "ifood" && <Nota>Pedido do iFood com entrega pelo iFood não tem as etapas “esperando sair” e “na rua”: só entram os que a loja entregou.</Nota>}
        {k.agendados > 0 && <Nota>{formatInt(k.agendados)} {k.agendados === 1 ? "pedido agendado ficou" : "pedidos agendados ficaram"} fora do tempo total (esperam de propósito).</Nota>}
        {k.em_andamento > 0 && <Nota>{formatInt(k.em_andamento)} {k.em_andamento === 1 ? "pedido ainda em andamento" : "pedidos ainda em andamento"} (sem tempo total).</Nota>}
        {k.sem_tempo > 0 && <Nota>{formatInt(k.sem_tempo)} {k.sem_tempo === 1 ? "pedido ficou" : "pedidos ficaram"} fora do tempo médio (concluído mais de 5h depois).</Nota>}
      </div>
    </>
  );
}
